import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as moment from 'moment';
import { GOAL_DATE } from '../../../constants/student-constants';
import { CloudServiceService } from '../../cloud/cloud-service.service';

@Injectable({
	providedIn: 'root'
})
export class CommonService {
	public dateSource: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public endDateCheck = this.dateSource.asObservable();

	constructor(private readonly cloudService: CloudServiceService) {}

	/**
     * @description check if the studentis existing one or a new user.
     * @param {Array} data list of students present.
     * @param {String} id unique identifier for the student.
     */
	public checkforExistingStudent(data: any[], id: string): Object {
		let _student: any;
		data.forEach((student) => {
			if (student.id === id && student.accepted_terms && student.accepted_terms === true) {
				_student = student;
			}
		});
		return _student;
	}

	/**
     * @description get the data from the collections in firestore and
     * format the data for populating the UI.
     * @param { Object }
     * @param { Object }
     */
	public formatStudentData(): Object {
		return;
	}

	public setEndDate(value) {
		this.dateSource.next(value);
	}

	/**
     * @description This is for override the exisiting student doc
     * @param user (Object) This is a incoming object
     * @param subtasks (Array) All subtask infromation
     * @param user_attrs (Object) 
     */
	public overridesFor(
		user: { upn: string; start_date: string; end_date: string; total_duration_avg: any },
		subtasks: any[],
		userAttrs: {}
	) {
		userAttrs['multiplier'] = 1;
		userAttrs['user_subtasks'] = [];
		userAttrs['upn'] = user.upn;

		// Default start or end dates if not provided
		if (!userAttrs.hasOwnProperty('start_date')) {
			userAttrs['start_date'] = user.start_date;
		}
		if (!userAttrs.hasOwnProperty('end_date')) {
			userAttrs['end_date'] = user.end_date;
		}
		if (!userAttrs.hasOwnProperty('total_duration_avg')) {
			userAttrs['total_duration_avg'] = user.total_duration_avg;
		}

		if (userAttrs['start_date'] && userAttrs['end_date']) {
			const start = moment(userAttrs['start_date'], 'YYYY-MM-DD');
			const end = moment(userAttrs['end_date'], 'YYYY-MM-DD');
			const duration: any = moment.duration(end.diff(start)).asDays().toFixed();

			userAttrs['multiplier'] = (duration / userAttrs['total_duration_avg']).toFixed(2);
		}

		userAttrs['user_subtasks'] = this.userSubtasksFor(user, subtasks, userAttrs['multiplier']);
		userAttrs['total_duration'] = userAttrs['user_subtasks'].reduce((prev, cur: { time_to_complete: any }) => {
			return prev + parseInt(cur.time_to_complete);
		}, 0);

		return userAttrs;
	}

	/**
     * @description this will prepare the user_subtasks filed.
     * @param user (Object) 
     * @param subtasks (Array)
     * @param multiplier (string)
     */
	public userSubtasksFor(user, subtasks, multiplier) {
		let timeDueFromStart = 0;
		const userSubtaskOverrides = [];

		subtasks.forEach((subtask) => {
			let userSubtaskAttrs = {};

			if (user.user_subtasks && user.user_subtasks.length > 0) {
				// existing user, needs overrides updated

				const userSubtask = user.user_subtasks.find((ust) => {
					return ust.subtask_id === subtask._id; // important to not be strict comparison here when dealing w/ mongo _ids
				});

				if (userSubtask) {
					userSubtaskAttrs = Object.assign({}, userSubtask);
					userSubtaskAttrs['time_to_complete'] = this.getComputedTimeToComplete(subtask, multiplier);
				} else {
					// new subtask
					userSubtaskAttrs = {
						subtask_id: subtask._id,
						time_to_complete: this.getComputedTimeToComplete(subtask, multiplier),
						activity: [],
						is_complete: false
					};
				}
			} else {
				// initial defaults / new subtask
				userSubtaskAttrs = {
					subtask_id: subtask._id,
					time_to_complete: this.getComputedTimeToComplete(subtask, multiplier),
					activity: [],
					is_complete: false
				};
			}
			timeDueFromStart += parseInt(userSubtaskAttrs['time_to_complete']);
			userSubtaskAttrs['time_due_from_start'] = timeDueFromStart;
			userSubtaskOverrides.push(userSubtaskAttrs);
		});

		return userSubtaskOverrides;
	}

	/**
     * @description will return time_to_complete field for each subtask
     * @param subtask (Object) Each subtask information
     * @param multiplier (String)
     */
	public getComputedTimeToComplete(subtask, multiplier) {
		let timeToComplete = subtask.time_to_complete_avg;
		const computedTimeToComplete = (subtask.time_to_complete_avg * multiplier).toFixed();

		// ensure at least minimum (or equal to)
		if (computedTimeToComplete >= subtask.time_to_complete_min) {
			timeToComplete = computedTimeToComplete;
		}

		// if it's less than the min range, use that number.
		if (computedTimeToComplete < subtask.time_to_complete_min) {
			timeToComplete = subtask.time_to_complete_min;
		}

		// ensure not higher than maximum
		if (computedTimeToComplete > subtask.time_to_complete_max) {
			timeToComplete = subtask.time_to_complete_max;
		}

		return parseInt(timeToComplete);
	}

	/**
     * 
     * @param subtasks 
     */
	public percentComplete(subtasks) {
		if (this.completedUnitsFor(subtasks) === 0) {
			return 0;
		}
		const percentAsInt = Math.round(this.completedUnitsFor(subtasks) / this.totalUnitsFor(subtasks) * 100);
		if (percentAsInt > 100) {
			return 100;
		} else {
			return percentAsInt;
		}
	}

	/**
     * 
     * @param subtasks 
     */
	public completedUnitsFor(subtasks) {
		return subtasks.filter((st) => st.is_complete).reduce((sum, subtask) => {
			if (!isNaN(parseInt(subtask.time_to_complete))) {
				return sum + parseInt(subtask.time_to_complete);
			} else {
				return sum + 0;
			}
		}, 0);
	}

	/**
     * 
     * @param subtasks 
     */
	public totalUnitsFor(subtasks) {
		return subtasks.reduce((sum, subtask) => {
			if (!isNaN(parseInt(subtask.time_to_complete))) {
				return sum + parseInt(subtask.time_to_complete);
			} else {
				return sum + 0;
			}
		}, 0);
	}

	/**
     * 
     * @param student 
     * @param subtasks 
     */
	public isBehind(student, subtasks) {
		return this.paceInDays(student, subtasks) < -13;
	}

	public pacingText(student, subtasks) {
		if (this.isBehind(student, subtasks)) {
			return 'Behind Goal';
		} else {
			return 'On Track for Goal';
		}
	}

	/**
     * 
     * @param student 
     * @param subtasks 
     */
	public paceInDays(student, subtasks) {
		if (!student.end_date) {
			return 0;
		}
		const expectedPace = moment(student.end_date).subtract(
			this.totalUnitsFor(subtasks) - this.completedUnitsFor(subtasks),
			'days'
		);
		return expectedPace.diff(moment(), 'days');
	}

	public disabledStartDates(student) {
		if (!student.end_date) {
			return {};
		} // else
		return {
			to: moment(student.end_date).subtract(student.total_duration_max, 'days').toDate(),
			from: moment(student.end_date).subtract(student.total_duration_min, 'days').toDate()
		};
	}

	/**
     * @description will return is the active task is the lastTask or not
     * @param activeTaskId (String) last task _id
     * @param studentData (Object) Student information
     */
	public wasLastTask(studentData: Object, tasks: Array<Object>) {
		if (tasks.length === studentData['user_tasks'].length) {
			return true;
		} else {
			return false;
		}
	}

	/**
     * @description will return is the active task is the lastTask or not
     * @param activeTaskId (String) last task _id
     * @param studentData (Object) Student information
     */
	public isLastTask(studentData: Object, tasks: Array<Object>) {
		if (studentData['user_tasks'] !== undefined) {
			if (tasks.length === studentData['user_tasks'].length + 1) {
				return true;
			} else {
				return false;
			}
		} else {
			return false;
		}
	}

	/**
    * @description This function will return lock status of current selected task
    * @param taskId (string) Seletected task _id
    */
	public checkTaskLockStatus(taskId, studentData) {
		const userTasks = studentData.user_tasks ? studentData.user_tasks : [];
		const taskStatus = userTasks.find((currentObject) => {
			if (currentObject.task_id === taskId) {
				return true;
			}
		});
		if (taskStatus) {
			return taskStatus;
		} else {
			return false;
		}
	}

	/**
  * @description This function will return a message object wich holds the information 
  * regarding task goal information with due text indicator.
  * @param task (Object) Selected task Information
  * @param isTaskLockedInfo (Object) current task lock information status
  */
	public displayTaskCompleteionInformation(task, isTaskLockedInfo, studentData) {
		const message = {
			goalDateMessage: '',
			dueSoonText: ''
		};
		const lastSubtask = JSON.parse(JSON.stringify(task.subtasks[task.subtasks.length - 1]));
		if (isTaskLockedInfo) {
			// If task locked then show recent lock activity date
			message.goalDateMessage = `COMPLETED: ${moment(isTaskLockedInfo.created_at).format('ll')}`;
		} else {
			if (lastSubtask) {
				const subtaskObject = studentData.user_subtasks.find((item) => {
					if (item.subtask_id === lastSubtask._id) {
						return item;
					}
					return undefined;
				});
				const now = moment().format('ll');
				const goalDate = moment(studentData.start_date).add(subtaskObject.time_due_from_start, 'days');
				message.goalDateMessage = `GOAL: ${goalDate.format('ll')}`;
				const future = goalDate.diff(now, 'days');

				if (future < 0) {
					message.dueSoonText = GOAL_DATE.PAST_GOAL;
				}
				if (future <= 10 && future > 1) {
					message.dueSoonText = future + GOAL_DATE.DAYS;
				}

				switch (future) {
					case 0: {
						message.dueSoonText = GOAL_DATE.TODAY;
						break;
					}
					case 1: {
						message.dueSoonText = GOAL_DATE.TOMORROW;
						break;
					}
				}
			}
		}
		return message;
	}

	/**
  * @description Will calculate the next pending phase and task for redirecting the user.
  * @param phases (array) Will hold all phases wth task property
  * @param currentTask (Object) will hold the current active task information.
  * @param studentData (Object) will hold student doc data
  */
	calculateNextPahseAndTask(phases, currentTask, studentData, forUnchecked: boolean) {
		let nextPhase; // will hold next phase information
		let nextTask; // Will hold next task information

		// loop through the all phases and check for
		// pending task and then get the pahse information as well.

		let phaseIndex = 0;
		while (phaseIndex < phases.length) {
			// each phase
			for (let taskIndex = 0; taskIndex < phases[phaseIndex].tasks.length; taskIndex++) {
				// each task
				const phaseObject = { ...phases[phaseIndex] };
				const taskObject = { ...phases[phaseIndex].tasks[taskIndex] };
				if (taskObject._id !== currentTask._id && !forUnchecked) {
					// get lock status info
					const lockedStatus = this.checkTaskStatus(taskObject, studentData);
					if (!lockedStatus) {
						// If not locked then this is the pending task.
						nextPhase = phaseObject;
						nextTask = taskObject;
						break; // braek for..loop
					} // End of if
				} // End of if

				if (taskObject._id === currentTask._id && forUnchecked) {
					// If not locked then this is the pending task.
					nextPhase = phaseObject;
					nextTask = taskObject;
					break; // braek for..loop
				} // End of if
			} // End of for
			if (nextPhase && nextTask) {
				break; // braek while loop
			}
			phaseIndex++;
		} // End of while loop
		if (!nextPhase && !nextTask) {
			nextPhase = phases[0];
			nextTask = phases[0].tasks[0];
		}
		return {
			nextPhase,
			nextTask
		};
	}

	/**
     * @description This function will check that the task status is locked or not
     * @param task (Object) Task Object
     * @param studentData (Object) Student doc data
     */
	checkTaskStatus(task, studentData) {
		if (studentData.user_tasks) {
			const lockedTaskInfo = studentData.user_tasks.find((lockedTask) => {
				if (lockedTask.task_id === task._id) {
					return lockedTask;
				}
			});
			if (lockedTaskInfo) {
				return true;
			}
			return false;
		} else {
			return false;
		}
	}

	/**
     * @description will redirect the current pahse and current task based on the current_phase and current_task
     * @param currentTask (Object) will hold the current active task information.
     * @param phases (array) Will hold all phases wth task property
     * @param studentData (Object) Student information 
     */
	public getCurrentPhaseAndTask(phases, studentData) {
		let nextPhase; // will hold next phase information
		let nextTask; // Will hold next task information
		let phaseIndex = 0;
		while (phaseIndex < phases.length) {
			// each phase
			if (phases[phaseIndex].contentful_id === studentData.current_phase.contentful_id) {
				for (let taskIndex = 0; taskIndex < phases[phaseIndex].tasks.length; taskIndex++) {
					// each task
					const phaseObject = { ...phases[phaseIndex] };
					const taskObject = { ...phases[phaseIndex].tasks[taskIndex] };
					if (taskObject.contentful_id === studentData.current_task.contentful_id) {
						nextPhase = phaseObject;
						nextTask = taskObject;
						break; // braek for..loop
					}
				} // End of for
			}
			// If current phase and task found the exit from the loop
			if (nextPhase && nextTask) {
				break; // braek while loop
			}
			phaseIndex++;
		} // End of while loop

		return {
			nextPhase,
			nextTask
		};
	}

	public addtoStack(userId: string, event: string, logType: string, description) {
		const messageBody = description;
		this.cloudService.logToStack(
			userId,
			event,
			logType,
			messageBody,
			(response: object) => {
				// Response callback
			},
			(error: object) => {
				console.log(error);
			}
		);
	}
}
